// LiveStreamChatroomMessage.jsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import LiveStreamUserAvatar from '../container/LiveStreamUserAvatar.js';
import UserLevelBadge from '../container/UserLevelBadge.js';
import UserLivestreamBadges from '../container/UserLivestreamBadges.js';
import { USER_BADGE_SIZE } from '../component/UserLivestreamBadges.jsx';
import useKaraokeTranslation from '../hook/useKaraokeTranslation.js';
import Tooltip from '../component/Tooltip.jsx';
import Rating from '../component/Rating.jsx';

import { ButtonId } from '../resource/mixpanel.js';
import { liveStreamChatType } from '../resource/liveStreamConstants.js';
import numberWithCommas from '../resource/numberWithCommas.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { textColor, color, breakpoint } from '../style/variables.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import IconAddSource from '../../img/ic_command_yellow.svg';
import IconDeviceSource from '../../img/ic_lovense_badge_pink.svg';
import SuperChatIconSource from '../../img/ic-super-chat.svg';
import media from '../style/media.js';

const defaultArray = [];
const defaultFunction = () => null;

const tooltipStyle = {
  wrapper: {
    height: `${USER_BADGE_SIZE}px`,
    zIndex: 2,
  },
};

export const LiveStreamChatroomMessage = ({
  t,
  senderId = '',
  senderLevel = null,
  senderAvatarId = '',
  senderUsername = '',
  senderDisplayName = '',
  fetchUser = defaultFunction,
  openBlockUserModal = defaultFunction,
  toggleModal = defaultFunction,
  isStreamOwnerMessage = false,
  isStreamOwner = false,
  isDisplayNameEnabled = true,
  isMiniProfileEnabled = false,
  isMiniBadgeInfoOpen = false,
  streamId = '',
  type = '',
  rank = null,
  amount = null,
  diamonds = null,
  productId = null,
  giftKey = '',
  giftPrice = '-',
  giftName = '',
  giftDuration = '-',
  text = '',
  hasWidthParent = false,
  senderBadges = defaultArray,
  vipMessageLogStyle = EMPTY_OBJECT,
  deviceWidth = 0,
  ratingStepSize = null,
  vipLevelLivestreamNotifyThreshold = null,
}) => {
  const nextTick = useRef(null);
  useEffect(() => {
    if (senderId && !senderUsername) {
      nextTick.current = setTimeout(() => {
        fetchUser({ senderId });
      });
    }
    return () => clearTimeout(nextTick.current);
  }, [senderUsername, senderId, fetchUser]);

  const blockUser = useCallback(() => {
    if (isStreamOwner && !isStreamOwnerMessage) {
      openBlockUserModal({ userId: senderId });
    }
  }, [isStreamOwner, isStreamOwnerMessage, openBlockUserModal, senderId]);
  const karaokeDisplayTitle = useKaraokeTranslation({
    productId,
    title: giftName,
    isToy: liveStreamChatType.TOY_DEVICE === type,
  });
  const avatarElement = useMemo(() => {
    if (
      [
        liveStreamChatType.KARAOKE,
        liveStreamChatType.TOY_DEVICE,
        liveStreamChatType.RATING,
      ].includes(type)
    ) {
      return null;
    }
    if (diamonds) {
      return (
        <SuperChatIconContainer>
          <img src={SuperChatIconSource} alt="super chat" />
        </SuperChatIconContainer>
      );
    }
    return (
      <UserAvatarContainer>
        <UserAvatarWrapper>
          <LiveStreamUserAvatar
            streamId={streamId}
            userId={senderId}
            badgeSize={36}
            avatarSize={24}
            diamondSize={8}
            fontSize={12}
            type={type}
            senderAvatarId={senderAvatarId}
          />
        </UserAvatarWrapper>
      </UserAvatarContainer>
    );
  }, [diamonds, senderAvatarId, senderId, streamId, type]);
  const userElement = useMemo(() => {
    return (
      <UserInfo>
        <Tooltip
          style={tooltipStyle}
          position="bottom-center"
          renderHint={() => <TooltipText>Level {senderLevel}</TooltipText>}
          disabled={deviceWidth <= breakpoint.tablet}
          trigger="hover"
        >
          <UserLevelBadge level={senderLevel} showPointer={true} />
        </Tooltip>
        {isDisplayNameEnabled ? (
          <Username $type={type}>
            {senderDisplayName || `@${senderUsername}`}
            {senderDisplayName && (
              <SecondaryText>@{senderUsername}</SecondaryText>
            )}
          </Username>
        ) : (
          <Username>@{senderUsername}</Username>
        )}
        <UserLivestreamBadges badges={senderBadges} />
      </UserInfo>
    );
  }, [
    isDisplayNameEnabled,
    senderBadges,
    senderDisplayName,
    senderLevel,
    senderUsername,
    deviceWidth,
    type,
  ]);
  const messageElement = useMemo(() => {
    switch (type) {
      case liveStreamChatType.GIFT: {
        return (
          <MessageContent $type={type}>
            {userElement}
            {t('user_sent_gift', {
              ns: TranslationNamespace.GIFT,
            })}{' '}
            <MessageContentBoldText>
              {t(giftKey, {
                ns: TranslationNamespace.GIFT,
              })}
              {giftPrice
                ? `(${numberWithCommas({ number: giftPrice })})`
                : giftPrice}
            </MessageContentBoldText>
          </MessageContent>
        );
      }
      case liveStreamChatType.RATING: {
        return (
          <MessageContent $type={type}>
            <RatingWrapper>
              <Rating
                value={5}
                max={100 / ratingStepSize}
                iconSize={12}
                isReadOnly
              />
            </RatingWrapper>
            <MessageContentBoldText marginRight={4}>
              {t('livestream_chat_5_stars_rating', {
                ns: TranslationNamespace.LIVESTREAM,
              })}
            </MessageContentBoldText>
            <MessageContentBoldText marginRight={4}>
              {t('general_by', {
                ns: TranslationNamespace.GENERAL,
              })}
            </MessageContentBoldText>
            {userElement}
          </MessageContent>
        );
      }
      case liveStreamChatType.TICKET:
      case liveStreamChatType.EARLY_BIRD_TICKET: {
        const isTicket = type === liveStreamChatType.TICKET;
        return (
          <MessageContent $type={type}>
            {userElement}
            {t('show_buy_ticket', {
              ns: TranslationNamespace.LIVESTREAM,
            })}{' '}
            <MessageContentBoldText>
              {amount !== null
                ? t(isTicket ? 'show_tickets' : 'early_bird_ticket_setting', {
                    number: numberWithCommas({ number: amount }),
                    ns: TranslationNamespace.LIVESTREAM,
                  })
                : ''}
            </MessageContentBoldText>
          </MessageContent>
        );
      }
      case liveStreamChatType.KARAOKE:
      case liveStreamChatType.TOY_DEVICE: {
        return (
          <MessageContent $type={type}>
            <MessageCommendIcon
              src={
                type === liveStreamChatType.KARAOKE
                  ? IconAddSource
                  : IconDeviceSource
              }
              alt="command"
            />
            <MessageContentBoldText>
              {`${karaokeDisplayTitle} ${t('command_sec', {
                number: giftDuration,
                ns: TranslationNamespace.GIFT,
              })}`}
              {giftPrice
                ? `(${numberWithCommas({ number: giftPrice })})`
                : giftPrice}
              <Username $type={type}>
                {t('general_by', {
                  ns: TranslationNamespace.GENERAL,
                })}
              </Username>
              {userElement}
            </MessageContentBoldText>
          </MessageContent>
        );
      }
      case liveStreamChatType.USER_ENTERED: {
        return (
          <MessageContent $type={type}>
            {userElement}
            <MessageContentUserEnteredText>
              {text}
            </MessageContentUserEnteredText>
          </MessageContent>
        );
      }
      default: {
        return (
          <MessageContent $type={type} marginLeft={diamonds ? 0 : 8}>
            {diamonds ? (
              <>
                <SuperChatTitle>
                  {`${t('super_chat', {
                    ns: TranslationNamespace.LIVESTREAM,
                  })}(${diamonds})`}
                </SuperChatTitle>
                <SuperChatBy>
                  {t('super_chat_by', {
                    ns: TranslationNamespace.LIVESTREAM,
                  })}
                </SuperChatBy>
              </>
            ) : null}
            {userElement}
            <MessageContentText>{text}</MessageContentText>
          </MessageContent>
        );
      }
    }
  }, [
    amount,
    diamonds,
    giftDuration,
    giftKey,
    giftPrice,
    karaokeDisplayTitle,
    ratingStepSize,
    t,
    text,
    type,
    userElement,
  ]);

  const vipMessageLogBorderColor = vipMessageLogStyle?.['borderColor'];
  const vipMessageLogBackgroundColor = vipMessageLogStyle?.['backgroundColor'];

  return (
    <StyledLiveStreamChatroomMessage hasWidthParent={hasWidthParent}>
      <MessageWrapper
        data-element_id={ButtonId.LiveStream.LivestreamChatItem}
        onClick={() => {
          if (isMiniProfileEnabled) {
            if (isMiniBadgeInfoOpen) {
              toggleModal({
                modalId: 'MiniBadgeInfoModal',
                isOpen: false,
              });
            }
            return toggleModal({
              modalId: 'MiniProfileModal',
              isOpen: true,
              payload: {
                userId: senderId,
              },
            });
          }
          return blockUser();
        }}
        isClickable={
          isMiniProfileEnabled || (isStreamOwner && !isStreamOwnerMessage)
        }
        isStreamOwnerMessage={isStreamOwnerMessage}
        $rank={diamonds ? undefined : rank}
        $type={type}
        $vipMessageLogBorderColor={vipMessageLogBorderColor}
        $vipMessageLogBackgroundColor={vipMessageLogBackgroundColor}
        $shouldShowVipLivestreamEnterNotice={
          senderLevel >= vipLevelLivestreamNotifyThreshold
        }
      >
        {avatarElement}
        {messageElement}
      </MessageWrapper>
    </StyledLiveStreamChatroomMessage>
  );
};

LiveStreamChatroomMessage.propTypes = {
  streamId: PropTypes.string,
  isStreamOwner: PropTypes.bool,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  senderDisplayName: PropTypes.string,
  senderAvatarId: PropTypes.string,
  text: PropTypes.string,
  type: PropTypes.string,
  giftName: PropTypes.string,
  giftKey: PropTypes.string,
  amount: PropTypes.number,
  diamonds: PropTypes.number,
  rank: PropTypes.number,
  deviceWidth: PropTypes.number,
  giftPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  giftDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isDisplayNameEnabled: PropTypes.bool,
  isMiniProfileEnabled: PropTypes.bool,
  isMiniBadgeInfoOpen: PropTypes.bool,
  isStreamOwnerMessage: PropTypes.bool,
  fetchUser: PropTypes.func,
  openBlockUserModal: PropTypes.func,
  toggleModal: PropTypes.func,
  t: PropTypes.func.isRequired,
  senderLevel: PropTypes.number,
  productId: PropTypes.string,
  senderBadges: PropTypes.array,
  ratingStepSize: PropTypes.number,
  hasWidthParent: PropTypes.bool,
  vipMessageLogStyle: PropTypes.object,
  vipLevelLivestreamNotifyThreshold: PropTypes.number,
};

const StyledLiveStreamChatroomMessage = styled.div`
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
  ${media.tablet`
    width: ${({ hasWidthParent }) => (hasWidthParent ? '100%' : '88%')};
    margin-top: 4px;
    margin-bottom: 4px;
  `}
`;

const MessageWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  min-height: 32px;
  position: relative;
  border-radius: 4px;
  ${({ isClickable }) => (isClickable ? 'cursor: pointer;' : '')}
  ${({
    $type,
    $rank,
    $vipMessageLogBackgroundColor,
    $shouldShowVipLivestreamEnterNotice,
  }) => {
    if ($type === liveStreamChatType.TOY_DEVICE) {
      return css`
        border: 1px solid #f6418d;
        background-color: rgba(35, 35, 35, 0.4);
      `;
    }
    if ($type === liveStreamChatType.KARAOKE) {
      return css`
        border: 1px solid #ffd100;
        background-color: rgba(35, 35, 35, 0.4);
      `;
    }
    if ($type === liveStreamChatType.RATING) {
      return css`
        min-height: 28px;
        background-color: rgba(255, 209, 0, 0.2);
      `;
    }
    if (
      [liveStreamChatType.USER_ENTERED, liveStreamChatType.CHAT].includes(
        $type
      ) &&
      $shouldShowVipLivestreamEnterNotice
    ) {
      return css`
        position: relative;
        :before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: 4px;
          background: ${({ $vipMessageLogBorderColor }) =>
            $vipMessageLogBorderColor};
          pointer-events: none;
          z-index: 0;
        }
        /* Use :after to cover the center area with parent's background color, creating a border effect */
        :after {
          content: '';
          position: absolute;
          top: 1px;
          right: 1px;
          bottom: 1px;
          left: 1px;
          border-radius: 3px;
          background-color: ${color.grey[900]};
          background-image: ${$vipMessageLogBackgroundColor};
          pointer-events: none;
          z-index: 0;
        }
        > * {
          position: relative;
          z-index: 1;
        }
      `;
    }

    switch ($rank) {
      case 1:
        return css`
          background-color: rgba(105, 94, 0, 0.4);
          border: solid 1px rgba(247, 235, 132, 0.4);
        `;
      case 2:
        return 'background-color: rgba(66, 118, 13, 0.4); border: solid 1px rgba(191,235,145, 0.4);';
      case 3:
      case 4:
      case 5:
        return 'background-color: rgba(130, 43, 43, 0.4); border: solid 1px rgba(254,180,180, 0.4);';
      case 6:
      case 7:
      case 8:
      case 9:
      case 10:
        return 'background-color: rgba(52, 47, 110, 0.4); border: solid 1px rgba(193,188,250, 0.4);';
      default:
        return 'background-color: rgba(35, 35, 35, 0.4);';
    }
  }}

  ${({ isStreamOwnerMessage }) =>
    isStreamOwnerMessage
      ? css`
          background-color: rgba(74, 144, 226, 0.6);
        `
      : ''};
`;

const MessageContent = styled.div`
  margin-left: ${({ marginLeft = 8 }) => marginLeft}px;
  font-size: 12px;
  line-height: ${USER_BADGE_SIZE}px;
  word-break: break-word;
  float: left;
  ${({ $type }) => {
    if ($type === liveStreamChatType.USER_ENTERED) {
      return css`
        color: ${color.yellow};
      `;
    }
    if ($type === liveStreamChatType.GIFT) {
      return css`
        color: ${textColor.primary};
      `;
    }
    if ($type === liveStreamChatType.RATING) {
      return css`
        margin-left: 0px;
        /* display: flex; */
        /* align-items: center; */
      `;
    }
    if (
      $type === liveStreamChatType.KARAOKE ||
      $type === liveStreamChatType.TOY_DEVICE
    ) {
      return css`
        color: ${$type === liveStreamChatType.KARAOKE
          ? '#fcd417'
          : color.lovense};
        margin-left: 0px;
        display: flex;
        align-items: flex-start;
        line-height: unset;
      `;
    }
    if ($type === liveStreamChatType.TICKET) {
      return css`
        color: ${color.peach};
      `;
    }
    if ($type === liveStreamChatType.EARLY_BIRD_TICKET) {
      return css`
        color: ${color.sky};
      `;
    }
  }}
  ::after {
    clear: both;
  }
`;

const MessageCommendIcon = styled.img`
  margin: 2px 8px 0 0; // For user info and chat message vertical alignment fine tuning
`;

const MessageContentText = styled.span`
  vertical-align: middle;
`;

const MessageContentUserEnteredText = styled(MessageContentText)`
  color: ${color.white};
  font-size: 14px;
`;

const MessageContentBoldText = styled(MessageContentText)`
  margin-left: ${({ marginLeft = 0 }) => marginLeft}px;
  margin-right: ${({ marginRight = 0 }) => marginRight}px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 5px;
  line-height: 18px; // For user info and chat message vertical alignment fine tuning
`;

const UserInfo = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-right: 4px;
  vertical-align: middle;
`;

const Username = styled.span`
  font-weight: 600;
  color: ${textColor.white};
  ${({ $type }) => {
    if (
      $type === liveStreamChatType.TOY_DEVICE ||
      $type === liveStreamChatType.KARAOKE
    ) {
      return css`
        font-weight: 400;
      `;
    }
  }}
`;

const UserAvatarContainer = styled.div`
  position: relative;
  align-self: start;
  height: 24px;
  width: 24px;
  min-width: 24px;
`;

const UserAvatarWrapper = styled.div`
  position: absolute;
  top: -6px;
  left: -6px;
`;

const SecondaryText = styled.span`
  color: var(--font-color-secondary-on-dark, rgba(221, 221, 221, 0.8));
  padding-left: 2px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.012px;
`;

const TooltipText = styled.div`
  position: absolute;
  z-index: 1;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${color.neutral[100]};
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 12px;
  transform: translate(-50%, -52px);
`;

const RatingWrapper = styled.div`
  margin-right: 4px;
  display: inline-flex;
  align-items: center;
  float: left;
  height: 20px;
`;

const SuperChatTitle = styled.span`
  margin-right: 4px;
  display: inline-flex;
  color: ${color.tealBlue};
  font-size: 12px;
  line-height: ${USER_BADGE_SIZE}px;
  word-break: break-word;
`;

const SuperChatBy = styled.span`
  margin-right: 4px;
  display: inline-flex;
  font-size: 12px;
  line-height: ${USER_BADGE_SIZE}px;
  word-break: break-word;
`;

const SuperChatIconContainer = styled.div`
  margin-right: 4px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${USER_BADGE_SIZE}px;
  > img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

export default withTranslation()(LiveStreamChatroomMessage);
